import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown,
  Menu,
  X,
  User,
  ShieldCheck,
  LogOut,
  Clipboard,
  BookOpen,
  CalendarRange,
  Info,
  PlusCircle,
  Database,
  Library,
  AlertCircle,
  Compass,
  Target,
  Mail,
  BarChart3,
  Globe,
  TrendingUp,
  Users,
  Briefcase,
  HelpCircle,
  FileText,
  UserPlus,
  Building,
  FolderKanban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ArterraLogo } from '@/components/ui/ArterraLogo';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { DOCUMENT_STUDIO, DOC_STUDIO_ENABLED } from '@/config/features';

interface NavLinkItem {
  id: string;
  label: string;
  to: string;
  icon?: React.ReactNode;
  description?: string;
}

interface NavDropdownItem {
  id: string;
  label: string;
  to?: string;
  icon?: React.ReactNode;
  items: NavLinkItem[];
}

function isNavLinkItem(item: NavLinkItem | NavDropdownItem): item is NavLinkItem {
  return 'to' in item && item.to !== undefined && !('items' in item);
}

function useActivePath() {
  const location = useLocation();
  const isActive = (to: string) => {
    const current = location.pathname || '/';
    if (to === '/') return current === '/';
    return current.startsWith(to);
  };
  return { location, isActive };
}

export const Navbar = React.memo(() => {
  const { location, isActive } = useActivePath();
  const { user, isAdmin, isApproved, userRole, clearCacheAndLogout } = useAuth();

  // Visual state
  const [scrolled, setScrolled] = useState(false);
  const [navbarReady, setNavbarReady] = useState(false);

  // Menus state
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close on outside click
  const rootRef = useRef<HTMLDivElement>(null);
  const mobileSheetRef = useRef<HTMLDivElement>(null);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      const target = e.target as HTMLElement | null;
      const clickInsideNavbar = rootRef.current.contains(target as Node);
      const clickInsideDropdownContent = !!target?.closest('[data-dropdown-content="true"]');
      const clickInsideMobileSheet = !!target?.closest('[data-mobile-sheet="true"]');

      if (!clickInsideNavbar && !clickInsideDropdownContent && !clickInsideMobileSheet) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setNavbarReady(true), []);

  // Close mobile menu on route changes
  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  // Keep keyboard and pointer interaction inside the modal mobile sheet.
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const menuTrigger = mobileMenuTriggerRef.current;
    const appRoot = document.getElementById('root');
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    document.body.style.overflow = 'hidden';
    appRoot?.setAttribute('inert', '');

    const focusFirstControl = () => {
      const firstControl = mobileSheetRef.current?.querySelector<HTMLElement>(focusableSelector);
      firstControl?.focus();
    };
    const focusFrame = window.requestAnimationFrame(focusFirstControl);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const controls = Array.from(
        mobileSheetRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? [],
      ).filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1);
      if (controls.length === 0) return;

      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      if (!mobileSheetRef.current?.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      appRoot?.removeAttribute('inert');
      document.removeEventListener('keydown', onKey);
      const focusTarget = previouslyFocused?.isConnected ? previouslyFocused : menuTrigger;
      focusTarget?.focus();
    };
  }, [mobileOpen]);

  // Keep dropdown open while hovering navbar or dropdown content; close only after leaving both
  useEffect(() => {
    if (!openMenu) return;
    let closeTimer: ReturnType<typeof setTimeout> | null = null;

    const evaluateHoverState = () => {
      const overNavbar = !!rootRef.current?.matches(':hover');
      const dropdownEl = document.querySelector('[data-dropdown-content="true"]') as HTMLElement | null;
      const overDropdown = !!dropdownEl?.matches(':hover');
      const isOver = overNavbar || overDropdown;

      if (isOver) {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      } else if (!closeTimer) {
        closeTimer = setTimeout(() => setOpenMenu(null), 150);
      }
    };

    const onMouseMove = () => evaluateHoverState();
    const onScrollAny = () => evaluateHoverState();

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseleave', onMouseMove);
    document.addEventListener('scroll', onScrollAny, true);
    // Initial check
    evaluateHoverState();

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseMove);
      document.removeEventListener('scroll', onScrollAny, true);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [openMenu]);

  const userState = useMemo(
    () => ({
      isAuthenticated: !!user,
      email: user?.email ?? null,
      isAdmin,
      isApproved,
      // ⑤B: owners are the `developer` role — they get a "My Projects" entry to
      // their owner dashboard (/owner). A signed-in developer only.
      isDeveloper: !!user && userRole === 'developer',
    }),
    [user, isAdmin, isApproved, userRole]
  );

  const handleLogout = useCallback(async () => {
    try {
      await clearCacheAndLogout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Error logging out');
    }
  }, [clearCacheAndLogout]);

  // Navigation content with better organization and descriptions
  const primaryNav: (NavLinkItem | NavDropdownItem)[] = useMemo(() => {
    // Determine routes based on auth state
    const investRoute = userState.isAuthenticated ? '/projects' : '/invest';
    const submitRoute = userState.isAuthenticated ? '/submit-project' : '/submit';
    const studioRoute = userState.isAuthenticated ? DOCUMENT_STUDIO.route : '/studio-landing';

    // ⑤B: a signed-in developer (owner) gets a "My Projects" entry to their owner
    // dashboard. Anonymous viewers + non-developers never see it (the dashboard is
    // role-gated; ⑤A is the real authz).
    const ownerNav: NavLinkItem[] = userState.isDeveloper
      ? [
          {
            id: 'my-projects',
            label: 'My Projects',
            to: '/owner',
            icon: <FolderKanban className="h-4 w-4" />,
          },
        ]
      : [];

    return [
      {
        // Public Raises directory (sub-project ③B). The investor-facing discovery
        // surface — anonymous-friendly, so it points at `/raises` regardless of
        // auth state (the ③A API resolves the viewer's tier server-side). Labelled
        // "Marketplace" (was "Explore") to mirror the product's core positioning —
        // "the infrastructure capital marketplace"; the `explore-raises` id is the
        // internal wiring and stays.
        id: 'explore-raises',
        label: 'Marketplace',
        to: '/raises',
        icon: <Compass className="h-4 w-4" />,
      },
      ...ownerNav,
      {
        id: 'for-investors',
        label: 'Invest',
        to: investRoute,
        icon: <Target className="h-4 w-4" />,
        items: [
          { id: 'deploy-capital', label: 'Deploy capital smarter and faster', to: investRoute, icon: <TrendingUp className="h-4 w-4" />, description: 'Access vetted infrastructure opportunities globally' },
          { id: 'explore-origination', label: 'Centralize deal sourcing', to: investRoute, icon: <Globe className="h-4 w-4" />, description: 'Unbiased enriched data for better decisions' },
          { id: 'enriched-data', label: 'AI-powered analysis', to: investRoute, icon: <Database className="h-4 w-4" />, description: 'Bank-grade security and global coverage' },
        ],
      },
      {
        id: 'for-owners',
        label: 'Submit',
        to: submitRoute,
        icon: <PlusCircle className="h-4 w-4" />,
        items: [
          { id: 'upload', label: 'Upload', to: submitRoute, icon: <PlusCircle className="h-4 w-4" />, description: 'PDFs, XLSX, PPT. We accept raw data rooms.' },
          { id: 'standardize', label: 'Standardize & Validate', to: submitRoute, icon: <Target className="h-4 w-4" />, description: 'Auto-extract terms, flag gaps, generate a completeness checklist.' },
          { id: 'publish', label: 'Publish Listing', to: submitRoute, icon: <BarChart3 className="h-4 w-4" />, description: 'Investor-ready profile with benchmarks and risk notes.' },
        ],
      },
      ...(DOC_STUDIO_ENABLED
        ? [
            {
              id: 'document-studio',
              label: 'Studio',
              to: studioRoute,
            },
          ]
        : []),
      {
        id: 'intelligence-platform',
        label: 'Platform',
        to: '/intelligence-platform',
        icon: <BarChart3 className="h-4 w-4" />,
        items: [
          {
            id: 'global-infrastructure',
            label: 'Global Infrastructure',
            to: '/intelligence/global-infrastructure',
            icon: <Globe className="h-4 w-4" />,
            description: 'Global market insights and trends'
          },
          {
            id: 'investor-intelligence',
            label: 'Investor Intelligence',
            to: '/intelligence/investor',
            icon: <TrendingUp className="h-4 w-4" />,
            description: 'Investment analytics and data'
          },
        ],
      },
      {
        id: 'company',
        label: 'Company',
        items: [
          { id: 'about', label: 'About Us', to: '/about', icon: <Info className="h-4 w-4" />, description: 'Our mission and vision' },
          { id: 'team', label: 'Our Team', to: '/team', icon: <Users className="h-4 w-4" />, description: 'Meet our leadership' },
          { id: 'careers', label: 'Careers', to: '/careers', icon: <Briefcase className="h-4 w-4" />, description: 'Join our team' },
          { id: 'contact', label: 'Contact', to: '/contact', icon: <Mail className="h-4 w-4" />, description: 'Get in touch with us' },
        ],
      },
      {
        id: 'resources',
        label: 'Resources',
        items: [
          { id: 'blog', label: 'Blog', to: '/blog', icon: <BookOpen className="h-4 w-4" />, description: 'Latest insights and news' },
          { id: 'publications', label: 'Publications', to: '/publications', icon: <FileText className="h-4 w-4" />, description: 'Reports and press releases' },
          { id: 'case-studies', label: 'Case Studies', to: '/case-studies', icon: <FileText className="h-4 w-4" />, description: 'Success stories' },
          { id: 'help', label: 'Help Center', to: '/help', icon: <HelpCircle className="h-4 w-4" />, description: 'Get support' },
          { id: 'docs', label: 'Documentation', to: '/docs', icon: <Database className="h-4 w-4" />, description: 'Technical documentation' },
          { id: 'get-started', label: 'Get Started', to: '/register', icon: <UserPlus className="h-4 w-4" />, description: 'Create an account' },
        ],
      },
    ];
  }, [userState.isAuthenticated, userState.isDeveloper]);

  const NavPill = ({ item }: { item: NavLinkItem }) => {
    const active = isActive(item.to);
    const base = 'px-4 py-2 text-[15px] font-medium transition-colors duration-150 relative inline-flex items-center';
    const color = active
      ? 'text-white'
      : 'text-slate-300 hover:text-white';

    return (
      <Link to={item.to} className={`${base} ${color}`} aria-current={active ? 'page' : undefined}>
        {item.label}
        {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />}
      </Link>
    );
  };

  const NavDropdown = React.memo(({ menu }: { menu: NavDropdownItem }) => {
    const active = menu.items.some((i) => isActive(i.to));
    const isOpen = openMenu === menu.id;
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = useCallback(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setOpenMenu(menu.id);
    }, [menu.id]);

    const handleMouseLeave = useCallback(() => {
      closeTimeoutRef.current = setTimeout(() => {
        setOpenMenu((prev) => (prev === menu.id ? null : prev));
      }, 150);
    }, [menu.id]);

    return (
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-dropdown-wrapper="true"
      >
        <button
          onClick={() => setOpenMenu((prev) => (prev === menu.id ? null : menu.id))}
          aria-expanded={isOpen}
          className={`px-4 py-2 text-[15px] font-medium inline-flex items-center gap-1 transition-colors duration-150 relative ${
            active || isOpen
              ? 'text-white'
              : 'text-slate-300 hover:text-white'
          }`}
        >
          <span>{menu.label}</span>
          <ChevronDown className={`h-3 w-3 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} />
          {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />}
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-full left-0 mt-2 w-[220px] rounded-lg bg-slate-800 border border-slate-700 shadow-xl overflow-hidden z-50"
              data-dropdown-content="true"
            >
              <div className="py-1.5">
                {menu.items.map((i) => (
                  <Link
                    key={i.id}
                    to={i.to}
                    onClick={() => setOpenMenu(null)}
                    className="group flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/60 transition-colors duration-100"
                  >
                    {i.icon && (
                      <span className="flex-shrink-0 text-slate-500 group-hover:text-amber-400 transition-colors">
                        {i.icon}
                      </span>
                    )}
                    <span>{i.label}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }, (prev, next) => {
    return prev.menu.id === next.menu.id;
  });

  const AuthMenu = () => {
    if (!userState.isAuthenticated) {
      return (
        <Link
          to="/login"
          className="px-4 py-2 rounded-md text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-all duration-150 inline-flex items-center gap-2"
        >
          <User className="h-4 w-4" />
          <span>Login</span>
        </Link>
      );
    }

    const initial = userState.email?.charAt(0).toUpperCase() ?? '?';
    const isOpen = openMenu === 'user';

    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpenMenu((p) => (p === 'user' ? null : 'user'));
          }}
          className="group relative flex items-center gap-1.5 focus:outline-none"
          aria-label="Open account menu"
          aria-expanded={isOpen}
        >
          <div className="relative">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm transition-all duration-200 ring-2 ring-slate-800 ${
              isOpen
                ? 'bg-gray-200 text-gray-900'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
              {initial}
            </div>
            {userState.isAdmin && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white" />
            )}
          </div>
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-all duration-200 hidden sm:block ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="absolute right-0 mt-3 w-[260px] rounded-lg bg-white shadow-lg ring-1 ring-gray-200 overflow-hidden z-50"
              data-dropdown-content="true"
            >
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="text-sm font-medium text-gray-900 truncate">{userState.email}</div>
                <div className="flex items-center gap-1.5 mt-1">
                  {userState.isAdmin && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                      Admin
                    </span>
                  )}
                  {userState.isApproved && !userState.isAdmin && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      Approved
                    </span>
                  )}
                  {!userState.isApproved && !userState.isAdmin && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                      Pending
                    </span>
                  )}
                </div>
              </div>

              <div className="py-2">
                <Link
                  to="/profile"
                  onClick={() => setOpenMenu(null)}
                  className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                  <User className="h-4 w-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                  Profile
                </Link>

                <Link
                  to="/organization"
                  onClick={() => setOpenMenu(null)}
                  className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                  <Building className="h-4 w-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                  Organization
                </Link>

                <Link
                  to="/dms"
                  onClick={() => setOpenMenu(null)}
                  className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                  <Library className="h-4 w-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                  Document Management System
                </Link>

                {DOC_STUDIO_ENABLED && (
                  <Link
                    to={DOCUMENT_STUDIO.route}
                    onClick={() => setOpenMenu(null)}
                    className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <FileText className="h-4 w-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                    Document Studio
                  </Link>
                )}

                <Link
                  to="/project-notes"
                  onClick={() => setOpenMenu(null)}
                  className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                  <Clipboard className="h-4 w-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                  Project Notes
                </Link>

                {(userState.isApproved || userState.isAdmin) && (
                  <Link
                    to="/submit-project"
                    onClick={() => setOpenMenu(null)}
                    className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <PlusCircle className="h-4 w-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                    Create Project
                  </Link>
                )}

                {userState.isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setOpenMenu(null)}
                    className="group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <ShieldCheck className="h-4 w-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                    Admin Dashboard
                  </Link>
                )}

                {!userState.isApproved && !userState.isAdmin && (
                  <div className="flex items-center gap-3 px-4 py-2.5 mx-2 my-1 rounded-md text-sm font-medium text-amber-800 bg-amber-50">
                    <AlertCircle className="h-4 w-4" />
                    Approval Pending
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100">
                <button
                  onClick={() => {
                    handleLogout();
                    setOpenMenu(null);
                  }}
                  className="w-full group flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-150"
                >
                  <LogOut className="h-4 w-4 text-gray-400 group-hover:text-red-600 transition-colors" />
                  Logout
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const DesktopNav = () => {
    const exploreLink = primaryNav.find((i) => i.id === 'explore-raises') as NavLinkItem | undefined;
    // ⑤B owner dashboard entry (developers only; absent otherwise).
    const myProjectsLink = primaryNav.find((i) => i.id === 'my-projects') as NavLinkItem | undefined;
    const investLink = primaryNav.find((i) => i.id === 'for-investors') as NavDropdownItem;
    const submitLink = primaryNav.find((i) => i.id === 'for-owners') as NavDropdownItem;
    const documentStudioLink = primaryNav.find((i) => i.id === 'document-studio') as NavLinkItem | undefined;
    const intelligencePlatform = primaryNav.find((i) => i.id === 'intelligence-platform') as NavDropdownItem;
    const groupedMenus = primaryNav.filter((i) => !isNavLinkItem(i) && i.id !== 'intelligence-platform' && i.id !== 'for-owners' && i.id !== 'for-investors') as NavDropdownItem[];

    // Invest and Submit are plain links (no dropdown)
    const investTo = userState.isAuthenticated ? '/projects' : '/invest';
    const submitTo = userState.isAuthenticated ? '/submit-project' : '/submit';

    return (
      // Only the center column flexes; its dropdowns must remain unclipped.
      <div className="hidden lg:flex min-w-0 flex-1 items-center justify-center gap-0.5">
        {exploreLink && <NavPill key={exploreLink.id} item={exploreLink} />}
        {myProjectsLink && <NavPill key={myProjectsLink.id} item={myProjectsLink} />}
        {investLink && <NavPill item={{ id: 'for-investors', label: 'Invest', to: investTo }} />}
        {submitLink && <NavPill item={{ id: 'for-owners', label: 'Submit', to: submitTo }} />}
        {documentStudioLink && <NavPill key={documentStudioLink.id} item={documentStudioLink} />}

        {intelligencePlatform && <NavDropdown key={intelligencePlatform.id} menu={intelligencePlatform} />}

        <span aria-hidden="true" className="mx-2 h-4 w-px bg-slate-600" />

        {groupedMenus.map((menu) => (
          <NavDropdown key={menu.id} menu={menu} />
        ))}
      </div>
    );
  };

  const MobileSheet = () => {
    const [expandedSection, setExpandedSection] = useState<string | null>(null);

    if (!mobileOpen) return null;
    return createPortal(
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            ref={mobileSheetRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000]"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation menu"
            data-mobile-sheet="true"
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 220 }}
              className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl flex flex-col"
            >
              <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <ArterraLogo size={32} />
                  <span className="font-bold text-lg">
                    <span className="text-slate-900">Arterra</span>
                    <span className="text-slate-900 font-normal ml-1.5">AI</span>
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  className="h-9 w-9 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-5" role="navigation" aria-label="Mobile">
                {/* Navigation items - top-level with collapsible sub-items */}
                <div className="space-y-1">
                  {primaryNav.map((item) => {
                    // Simple link item (no children)
                    if (isNavLinkItem(item)) {
                      return (
                        <Link
                          key={item.id}
                          to={item.to}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center gap-3 px-3.5 py-3 rounded-lg text-[15px] font-medium transition-colors ${
                            isActive(item.to)
                              ? 'text-amber-600 bg-amber-50'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          {item.icon}
                          {item.label}
                        </Link>
                      );
                    }

                    // Dropdown item - collapsible accordion
                    const dropdownItem = item as NavDropdownItem;
                    const isExpanded = expandedSection === dropdownItem.id;
                    const hasActiveChild = dropdownItem.items.some((i) => isActive(i.to));

                    // If the dropdown has a direct link (e.g. Invest, Submit), show it as navigable + expandable
                    return (
                      <div key={dropdownItem.id}>
                        <button
                          onClick={() => setExpandedSection(isExpanded ? null : dropdownItem.id)}
                          className={`w-full flex items-center justify-between px-3.5 py-3 rounded-lg text-[15px] font-medium transition-colors ${
                            hasActiveChild || isExpanded
                              ? 'text-amber-600 bg-amber-50'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {dropdownItem.icon}
                            <span>{dropdownItem.label}</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="pl-4 py-1 space-y-0.5">
                                {dropdownItem.items.map((child) => (
                                  <Link
                                    key={child.id}
                                    to={child.to}
                                    onClick={() => setMobileOpen(false)}
                                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-colors ${
                                      isActive(child.to)
                                        ? 'text-amber-600 bg-amber-50'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                  >
                                    {child.icon}
                                    <span>{child.label}</span>
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                {/* User section */}
                <div className="border-t border-gray-100 pt-5 mt-5">
                  {!userState.isAuthenticated ? (
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3.5 py-3 rounded-lg text-[15px] font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                      <User className="h-4 w-4" />
                      Login
                    </Link>
                  ) : (
                    <>
                      <div className="px-3.5 pb-3 mb-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center font-medium text-sm">
                            {userState.email?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{userState.email}</div>
                            <div className="flex items-center gap-1.5 mt-1">
                              {userState.isAdmin && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                  Admin
                                </span>
                              )}
                              {userState.isApproved && !userState.isAdmin && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                  Approved
                                </span>
                              )}
                              {!userState.isApproved && !userState.isAdmin && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Link
                          to="/profile"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                        >
                          <User className="h-4 w-4" />
                          Profile
                        </Link>
                        <Link
                          to="/organization"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                        >
                          <Building className="h-4 w-4" />
                          Organization
                        </Link>
                        <Link
                          to="/dms"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                        >
                          <Library className="h-4 w-4" />
                          Document Management System
                        </Link>
                        {DOC_STUDIO_ENABLED && (
                          <Link
                            to={DOCUMENT_STUDIO.route}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                          >
                            <FileText className="h-4 w-4" />
                            Document Studio
                          </Link>
                        )}
                        <Link
                          to="/project-notes"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                        >
                          <Clipboard className="h-4 w-4" />
                          Project Notes
                        </Link>
                        {(userState.isApproved || userState.isAdmin) && (
                          <Link
                            to="/submit-project"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                          >
                            <PlusCircle className="h-4 w-4" />
                            Create Project
                          </Link>
                        )}
                        {userState.isAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                          >
                            <ShieldCheck className="h-4 w-4" />
                            Admin Dashboard
                          </Link>
                        )}
                        {!userState.isApproved && !userState.isAdmin && (
                          <div className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-amber-800 bg-amber-50">
                            <AlertCircle className="h-4 w-4" />
                            Approval Pending
                          </div>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full text-left flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-red-50 hover:text-red-600"
                        >
                          <LogOut className="h-4 w-4" />
                          Logout
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {!userState.isAuthenticated && (
                <div className="p-4 border-t border-gray-100">
                  <Link
                    to="/book-demo"
                    onClick={() => setMobileOpen(false)}
                    className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:border-gray-400 hover:text-gray-900 transition-colors gap-2"
                  >
                    <CalendarRange className="h-4 w-4" />
                    Book a Demo
                  </Link>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  if (!navbarReady) {
    return <div className="h-[74px] w-full" aria-label="Loading navigation" />;
  }

  return (
    <div
      ref={rootRef}
      className="fixed top-0 z-50 w-full px-3 pt-3 transition-all duration-200 pointer-events-none"
    >
      <nav
        className={`relative rounded-full bg-slate-900 backdrop-blur-lg border border-slate-700 transition-all duration-200 pointer-events-auto ${
          scrolled ? 'shadow-md' : 'shadow-sm'
        }`}
        aria-label="Main Navigation"
      >
        {/* Content-sized edges prevent the CTA/auth group from being clipped. */}
        <div className="px-4 sm:px-5 xl:px-6">
          <div className="flex items-center justify-between gap-3 h-[56px]">
            {/* Logo */}
            <Link to="/" className="flex shrink-0 items-center gap-3 group" aria-label="Home">
              <ArterraLogo size={40} />
              <div className="font-medium text-[22px] tracking-tight leading-none whitespace-nowrap">
                <span className="text-white">Arterra</span>
                <span className="text-white font-normal ml-1.5">AI</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <DesktopNav />

            {/* Right: CTA & Auth */}
            <div className="hidden lg:flex shrink-0 items-center justify-end gap-2">
              {!userState.isAuthenticated && (
                <Link
                  to="/book-demo"
                  className="px-4 py-2 rounded-md text-white text-sm font-medium hover:text-slate-200 transition-all duration-150 inline-flex items-center gap-2"
                >
                  <CalendarRange className="h-4 w-4" />
                  Book a Demo
                </Link>
              )}
              <AuthMenu />
            </div>

            {/* Mobile Menu Toggle */}
            <div className="shrink-0 lg:hidden">
              <Button
                ref={mobileMenuTriggerRef}
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                className="h-9 w-9 rounded-full hover:bg-white/10 text-white"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sheet */}
      <MobileSheet />
    </div>
  );
});
